/**
 * Aggregate Lifetime Stats Card
 * Fetches and sums lifetime mining stats across all accounts from ore-stats.com API
 */

import { useState, useEffect } from 'react';
import { PublicKey } from '@solana/web3.js';
import { MiningAccountSummary } from '../../api/client';
import { oreStatsFetchAll } from '../../utils/oreStatsClient';
import { Win98Panel } from '../Win98Panel';
import { formatNumber } from '../../utils/numberFormatting';
import Spinner from '../Spinner';
import { useTranslation } from '../../context/LanguageContext';

// Evore program ID for PDA derivation
const EVORE_PROGRAM_ID = new PublicKey('8jaLKWLJAj5jVCZbxpe3zRUvLB3LD48MRtaQ2AjfCfxa');

/**
 * Convert bigint to 8-byte little-endian buffer
 */
function bigintToLeBytes(value: bigint): Buffer {
  const buffer = Buffer.alloc(8);
  buffer.writeBigUInt64LE(value);
  return buffer;
}

/**
 * Derive the ManagedMinerAuth PDA from a manager pubkey
 * This is the address used by ore-stats.com for Evore accounts
 */
function getManagedMinerAuthPda(managerPubkey: string): string {
  const manager = new PublicKey(managerPubkey);
  const authId = 0n; // Always authId=0 for each manager
  const [managedMinerAuth] = PublicKey.findProgramAddressSync(
    [
      Buffer.from('managed-miner-auth'),
      manager.toBuffer(),
      bigintToLeBytes(authId),
    ],
    EVORE_PROGRAM_ID
  );
  return managedMinerAuth.toBase58();
}

interface Prices {
  solUsd: number;
  oreUsd: number;
}

interface AggregateLifetimeStatsCardProps {
  accounts: MiningAccountSummary[];
  prices: Prices | null;
  privyWalletAddress: string | null;
  minimized?: boolean;
  onMinimize?: () => void;
}

interface MinerStats {
  lifetime_deployed: number;
  lifetime_rewards_sol: number;
  lifetime_rewards_ore: number;
  round_id: number;
}

interface AggregatedLifetimeStats {
  totalDeployed: number;
  totalSolWon: number;
  totalOreWon: number;
  latestRoundId: number;
}

const LAMPORTS_PER_SOL = 1e9;
const ORE_DECIMALS = 1e11;

export function AggregateLifetimeStatsCard({ accounts, prices, privyWalletAddress, minimized = false, onMinimize }: AggregateLifetimeStatsCardProps) {
  const { t } = useTranslation();
  const [stats, setStats] = useState<AggregatedLifetimeStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    async function fetchAllStats() {
      if (accounts.length === 0) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);

        // Get miner addresses using same logic as individual view
        // - Legacy accounts: use privyWalletAddress
        // - Evore accounts: derive ManagedMinerAuthPda from managerPubkey
        const minerAddresses = accounts
          .map(a => {
            if (a.accountType === 'legacy') {
              return privyWalletAddress;
            }
            return getManagedMinerAuthPda(a.managerPubkey);
          })
          .filter((addr): addr is string => !!addr);

        // Remove duplicates
        const uniqueAddresses = [...new Set(minerAddresses)];

        if (uniqueAddresses.length === 0) {
          setStats(null);
          setLoading(false);
          return;
        }

        // Fetch stats for each miner with staggered requests and retry
        const paths = uniqueAddresses.map(addr => `/miner/${addr}`);
        const results = await oreStatsFetchAll<MinerStats>(paths);

        // Sum up all stats
        let totalDeployed = 0;
        let totalSolWon = 0;
        let totalOreWon = 0;
        let latestRoundId = 0;

        for (const result of results) {
          if (result) {
            totalDeployed += result.lifetime_deployed / LAMPORTS_PER_SOL;
            totalSolWon += result.lifetime_rewards_sol / LAMPORTS_PER_SOL;
            totalOreWon += result.lifetime_rewards_ore / ORE_DECIMALS;
            latestRoundId = Math.max(latestRoundId, result.round_id);
          }
        }

        if (totalDeployed === 0 && totalSolWon === 0 && totalOreWon === 0) {
          setStats(null);
        } else {
          setStats({ totalDeployed, totalSolWon, totalOreWon, latestRoundId });
        }
        setError(null);
      } catch (err) {
        console.error('Failed to fetch aggregate lifetime stats:', err);
        setError(err instanceof Error ? err.message : 'Failed to load stats');
      } finally {
        setLoading(false);
      }
    }

    fetchAllStats();
  }, [accounts, privyWalletAddress, retryCount]);

  if (loading) {
    return (
      <Win98Panel title={t('portfolio.lifetimeStats.title')} minimized={minimized} onMinimize={onMinimize}>
        <div className="win98-p-lg win98-text-center" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
          <Spinner />
          <span className="win98-text-muted">{t('portfolio.lifetimeStats.loading')}</span>
        </div>
      </Win98Panel>
    );
  }

  if (error) {
    return (
      <Win98Panel title={t('portfolio.lifetimeStats.title')} minimized={minimized} onMinimize={onMinimize}>
        <div className="win98-p-lg win98-text-center">
          <div className="win98-text-error win98-mb-md">{error}</div>
          <button
            className="win98-button"
            onClick={() => {
              setError(null);
              setRetryCount(c => c + 1);
            }}
          >
            {t('portfolio.lifetimeStats.retry')}
          </button>
        </div>
      </Win98Panel>
    );
  }

  if (!stats) {
    // If no accounts yet, show loading state (parent may still be fetching accounts)
    if (accounts.length === 0) {
      return (
        <Win98Panel title={t('portfolio.lifetimeStats.title')} minimized={minimized} onMinimize={onMinimize}>
          <div className="win98-p-lg win98-text-center" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
            <Spinner />
            <span className="win98-text-muted">{t('portfolio.lifetimeStats.loading')}</span>
          </div>
        </Win98Panel>
      );
    }
    // Only show "no history" if we have accounts but genuinely found no stats
    return (
      <Win98Panel title={t('portfolio.lifetimeStats.title')} minimized={minimized} onMinimize={onMinimize}>
        <div className="win98-p-lg win98-text-center win98-text-muted">
          {t('portfolio.lifetimeStats.emptyNoHistory')}
        </div>
      </Win98Panel>
    );
  }

  // Calculate values
  const netSolPnl = stats.totalSolWon - stats.totalDeployed;
  const netSolCost = stats.totalDeployed - stats.totalSolWon; // What you "spent" to get ORE
  const pnlPercent = stats.totalDeployed > 0 ? (netSolPnl / stats.totalDeployed) * 100 : 0;

  // USD calculations
  const deployedUsd = prices ? stats.totalDeployed * prices.solUsd : 0;
  const solWonUsd = prices ? stats.totalSolWon * prices.solUsd : 0;
  const oreWonUsd = prices ? stats.totalOreWon * prices.oreUsd : 0;
  const netSolPnlUsd = prices ? netSolPnl * prices.solUsd : 0;
  const netSolCostUsd = prices ? netSolCost * prices.solUsd : 0;

  // Accumulation cost calculations
  const costBasisSol = stats.totalOreWon > 0 ? netSolCost / stats.totalOreWon : 0;
  const costBasisUsd = stats.totalOreWon > 0 && prices ? netSolCostUsd / stats.totalOreWon : 0;
  const accumulationPnl = prices ? oreWonUsd - netSolCostUsd : 0;
  const accumulationPnlPercent = netSolCostUsd !== 0 ? (accumulationPnl / Math.abs(netSolCostUsd)) * 100 : 0;

  return (
    <Win98Panel title={t('portfolio.lifetimeStats.title')} minimized={minimized} onMinimize={onMinimize}>
      <div className="win98-p-md">
        {/* Lifetime Performance Section */}
        <div className="win98-section-heading win98-mb-xs">{t('portfolio.lifetimeStats.sectionPerformance')}</div>
        <div className="win98-sunken portfolio-sunken-group">
          <div className="portfolio-stat-grid-2col">
            <div className="portfolio-stat-item">
              <span className="portfolio-stat-label">{t('portfolio.lifetimeStats.statDeployed')}</span>
              <span className="portfolio-stat-value">{formatNumber(stats.totalDeployed, 4)} SOL</span>
              {prices && (
                <span className="portfolio-stat-subtext">${formatNumber(deployedUsd, 2)}</span>
              )}
            </div>
            <div className="portfolio-stat-item">
              <span className="portfolio-stat-label">{t('portfolio.lifetimeStats.statSolWon')}</span>
              <span className="portfolio-stat-value positive">{formatNumber(stats.totalSolWon, 4)} SOL</span>
              {prices && (
                <span className="portfolio-stat-subtext">${formatNumber(solWonUsd, 2)}</span>
              )}
            </div>
          </div>
          <div className="portfolio-stat-grid-2col win98-mt-sm">
            <div className="portfolio-stat-item">
              <span className="portfolio-stat-label">{t('portfolio.lifetimeStats.statOreWon')}</span>
              <span className="portfolio-stat-value win98-text-ore">{formatNumber(stats.totalOreWon, 4)} ORE</span>
              {prices && (
                <span className="portfolio-stat-subtext">${formatNumber(oreWonUsd, 2)}</span>
              )}
            </div>
            <div className="portfolio-stat-item">
              <span className="portfolio-stat-label">{t('portfolio.lifetimeStats.statNetSolPnl')}</span>
              <span className={`portfolio-stat-value ${netSolPnl >= 0 ? 'positive' : 'negative'}`}>
                {netSolPnl >= 0 ? '+' : ''}{formatNumber(netSolPnl, 4)} SOL
              </span>
              {prices && (
                <span className="portfolio-stat-subtext">
                  {netSolPnlUsd >= 0 ? '+' : ''}${formatNumber(netSolPnlUsd, 2)} ({pnlPercent >= 0 ? '+' : ''}{formatNumber(pnlPercent, 1)}%)
                </span>
              )}
            </div>
          </div>
        </div>

        {/* ORE Accumulation Cost Section */}
        {prices && stats.totalOreWon > 0 && (
          <>
            <div className="win98-section-heading win98-mb-xs">{t('portfolio.lifetimeStats.sectionAccumulationCost')}</div>
            <div className="win98-sunken portfolio-sunken-group">
              <div className="portfolio-stat-grid-2col">
                <div className="portfolio-stat-item">
                  <span className="portfolio-stat-label">{t('portfolio.lifetimeStats.statTotalCost')}</span>
                  {netSolCost > 0 ? (
                    <>
                      <span className="portfolio-stat-value">{formatNumber(netSolCost, 4)} SOL</span>
                      <span className="portfolio-stat-subtext">${formatNumber(netSolCostUsd, 2)}</span>
                    </>
                  ) : (
                    <>
                      <span className="portfolio-stat-value positive">{t('portfolio.lifetimeStats.costFree')}</span>
                      <span className="portfolio-stat-subtext">{t('portfolio.lifetimeStats.solProfit', { amount: formatNumber(Math.abs(netSolCost), 4) })}</span>
                    </>
                  )}
                </div>
                <div className="portfolio-stat-item">
                  <span className="portfolio-stat-label">{t('portfolio.lifetimeStats.statCostBasis')}</span>
                  {netSolCost > 0 ? (
                    <>
                      <span className="portfolio-stat-value">{formatNumber(costBasisSol, 4)} SOL/ORE</span>
                      <span className="portfolio-stat-subtext">${formatNumber(costBasisUsd, 2)}/ORE</span>
                    </>
                  ) : (
                    <>
                      <span className="portfolio-stat-value positive">{t('portfolio.lifetimeStats.zeroCostBasis')}</span>
                      <span className="portfolio-stat-subtext">{t('portfolio.lifetimeStats.freeAccumulation')}</span>
                    </>
                  )}
                </div>
              </div>
              <div className="portfolio-stat-grid-2col win98-mt-sm">
                <div className="portfolio-stat-item">
                  <span className="portfolio-stat-label">{t('portfolio.lifetimeStats.statCurrentOreValue')}</span>
                  <span className="portfolio-stat-value win98-text-ore">${formatNumber(oreWonUsd, 2)}</span>
                  <span className="portfolio-stat-subtext">{formatNumber(stats.totalOreWon, 4)} ORE</span>
                </div>
                <div className="portfolio-stat-item">
                  <span className="portfolio-stat-label">{t('portfolio.lifetimeStats.statPnl')}</span>
                  <span className={`portfolio-stat-value ${accumulationPnl >= 0 ? 'positive' : 'negative'}`}>
                    {accumulationPnl >= 0 ? '+' : ''}${formatNumber(accumulationPnl, 2)}
                  </span>
                  <span className="portfolio-stat-subtext">
                    {accumulationPnlPercent >= 0 ? '+' : ''}{formatNumber(accumulationPnlPercent, 1)}%
                  </span>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Data source */}
        <div className="win98-mt-md win98-text-small win98-text-muted win98-text-center">
          {t('portfolio.lifetimeStats.dataSourceBase')}{prices && t('portfolio.lifetimeStats.dataSourcePrices', { solPrice: formatNumber(prices.solUsd, 2), orePrice: formatNumber(prices.oreUsd, 2) })}
        </div>
      </div>
    </Win98Panel>
  );
}
